import { getKokoroTTSService } from './kokoroSingleton';
import { stripMarkdown } from './textChunker';

export class FaanaVoiceService {
  constructor(settings = {}) {
    this.recognition = null;
    this.synthesis = window.speechSynthesis;
    this.isListening = false;
    this.isRecording = false;
    this.bypassTimer = null;
    this.periodicRestartTimer = null;
    this.isBypassMode = false;

    // Settings from user preferences
    this.settings = {
      wakeWord: settings.wakeWord || 'hey coach',
      language: settings.language || 'en-US',
      tts: settings.tts || {},
      behavior: settings.behavior || {},
      advanced: settings.advanced || {},
      ...settings
    };

    // Audio feedback - embedded base64 sounds
    this.sounds = {
      // Ascending chime for wake word
      wakeWord: new Audio('data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ4AAAAHAA4AFAAZABoAGgASAA=='),
      // Double beep for record start
      recordStart: new Audio('data:audio/wav;base64,UklGRl4AAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToAAAAA//8AAP//AAD//wAA//8AAA=='),
      // Single beep for record stop
      recordStop: new Audio('data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ4AAAD//wEAAQD//w=='),
      // Success chime for message sent
      messageSent: new Audio('data:audio/wav;base64,UklGRoAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVwAAAAHAA4AFAAZABoAGgASAAcA'),
      // Error buzz
      error: new Audio('data:audio/wav;base64,UklGRmAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTwAAAAA//8AAP//AAD//wAA')
    };

    // Voice commands
    this.commands = {
      send: ['send now', 'send this now', 'send it now', 'send the message', 'submit', 'send message'],
      clear: ['clear message', 'cancel', 'clear', 'start over', 'delete message', 'clear text'],
      stop: ['stop listening', 'stop recording', 'stop', 'pause']
    };

    // Wake word variations
    this.wakeWordVariations = this.generateWakeWordVariations(this.settings.wakeWord);

    // Battery monitoring
    this.batteryLevel = 1;
    this.lowBatteryMode = false;

    // Kokoro TTS engine — shared singleton, start loading immediately
    this._kokoroTTS = getKokoroTTSService();
    this._kokoroTTS.init().catch((err) => {
      console.warn('[FaanaVoiceService] Kokoro model failed to load, will use native TTS fallback:', err.message);
    });

    // Persistent mic stream — keeps macOS mic indicator steady while
    // SpeechRecognition restarts its sessions every ~5-7s of silence.
    this._micStream = null;

    // Callbacks
    this.callbacks = {};

    // Initialize on construction
    this.initializeRecognition();
    this.initializeBatteryMonitoring();
  }

  generateWakeWordVariations(wakeWord) {
    const base = wakeWord.toLowerCase();
    const variations = [base];

    if (base === 'hey coach') {
      variations.push(
        'hi coach',
        'hay coach',
        'a coach'
      );
    }

    return variations;
  }

  initializeRecognition() {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('[FaanaVoiceService] Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.settings.language;
    this.recognition.maxAlternatives = 3;

    // Set up event handlers
    this.setupRecognitionHandlers();
  }

  setupRecognitionHandlers() {
    if (!this.recognition) return;

    // Track the result index that triggered wake word — all results at this
    // index are revisions of the same utterance and should be skipped in bypass mode
    this.wakeWordTriggerResultIndex = -1;
    // Track the last final transcript to avoid duplicates
    this.lastFinalTranscript = null;

    this.recognition.onresult = (event) => {
      const lastResultIndex = event.results.length - 1;
      const result = event.results[lastResultIndex];
      const transcript = result[0].transcript.trim();
      const isFinal = result.isFinal;


      // In bypass mode, everything goes through bypass handler
      if (this.isBypassMode) {
        this.handleBypassModeTranscript(transcript, isFinal, lastResultIndex);
        return;
      }

      // Wake word detection when not recording
      if (!this.isRecording && this.detectWakeWord(transcript)) {
        this.wakeWordTriggerResultIndex = lastResultIndex;
        this.handleWakeWord(transcript);
        return;
      }

      // When recording but not in bypass mode (shouldn't happen with current flow)
      if (this.isRecording && !this.isBypassMode) {
        if (isFinal) {
          const command = this.detectCommand(transcript);
          if (command) {
            this.handleCommand(command);
          } else {
            this.callbacks.onTranscript?.(transcript, isFinal);
          }
        } else {
          this.callbacks.onInterim?.(transcript);
        }
      }
    };

    // Track repeated network errors (Firefox sends audio to Google servers which often fails)
    let networkErrorCount = 0;

    this.recognition.onerror = (event) => {
      // 'aborted' fires when we call stop() or on initial Chrome quirk — always ignore
      if (event.error === 'aborted') return;

      // 'no-speech' fires when mic is open but user is silent — ignore, onend will restart
      if (event.error === 'no-speech') return;

      // Firefox: repeated network errors means SpeechRecognition is unsupported
      if (event.error === 'network') {
        networkErrorCount++;
        if (networkErrorCount >= 3) {
          console.error('[FaanaVoiceService] Speech recognition unavailable — requires Chrome or Edge');
          this.isListening = false;
          this.callbacks.onError?.('speech-recognition-unavailable');
          return;
        }
        // Will restart via onend
        return;
      }

      // Non-recoverable errors
      console.error('[FaanaVoiceService] Speech recognition error:', event.error);
      this.callbacks.onError?.(event.error);
    };

    // onend is the ONLY place we restart — no timers, no race conditions.
    // Chrome fires onend after: errors, stop() calls, and natural session ends.
    this.recognition.onend = () => {
      this.wakeWordTriggerResultIndex = -1;
      if (this.isListening) {
        try {
          this.recognition.start();
        } catch (error) {
          if (!error.message?.includes('already started')) {
            console.error('[FaanaVoiceService] Failed to restart:', error);
          }
        }
      }
    };
  }

  async initializeBatteryMonitoring() {
    if ('getBattery' in navigator) {
      try {
        const battery = await navigator.getBattery();
        this.batteryLevel = battery.level;

        battery.addEventListener('levelchange', () => {
          this.batteryLevel = battery.level;
          if (battery.level < 0.20 && !this.lowBatteryMode) {
            this.enterLowBatteryMode();
          } else if (battery.level >= 0.20 && this.lowBatteryMode) {
            this.exitLowBatteryMode();
          }
        });
      } catch (_error) {
        console.log('Battery API not available');
      }
    }
  }

  enterLowBatteryMode() {
    this.lowBatteryMode = true;
    if (this.isListening) {
      this.stop();
      this.callbacks.onLowBattery?.();
    }
  }

  exitLowBatteryMode() {
    this.lowBatteryMode = false;
  }

  detectWakeWord(transcript) {
    const lower = transcript.toLowerCase();

    // 1. Exact variation match (fast path)
    const exactMatch = this.wakeWordVariations.some(variation => {
      if (lower.includes(variation)) {
        const index = lower.indexOf(variation);
        if (index === 0 || (index > 0 && lower[index - 1] === ' ')) {
          return true;
        }
      }
      return false;
    });
    if (exactMatch) return true;

    return false;
  }

  detectCommand(transcript) {
    const lower = transcript.toLowerCase();

    // Check for send commands
    for (const cmd of this.commands.send) {
      if (lower.includes(cmd)) {
        return { type: 'send', text: this.removeCommand(transcript, this.commands.send) };
      }
    }

    // Check for clear commands
    for (const cmd of this.commands.clear) {
      if (lower.includes(cmd)) {
        return { type: 'clear', text: '' };
      }
    }

    // Check for stop commands
    for (const cmd of this.commands.stop) {
      if (lower.includes(cmd)) {
        return { type: 'stop', text: '' };
      }
    }

    return null;
  }

  removeCommand(transcript, commands) {
    let cleaned = transcript;
    commands.forEach(cmd => {
      const regex = new RegExp(cmd, 'gi');
      cleaned = cleaned.replace(regex, '');
    });
    return cleaned.trim();
  }

  handleWakeWord(_transcript) {
    this.playSound('wakeWord');
    this.isRecording = true;

    // Don't pass any text to the callback - wake word should not add any text
    this.callbacks.onWakeWord?.();

    // Enter bypass mode to keep listening for actual message
    this.enterBypassMode();

    // Start recording feedback after short delay
    setTimeout(() => {
      if (this.isRecording) {
        this.playSound('recordStart');
        this.callbacks.onRecordingStart?.();
      }
    }, 500);
  }

  handleBypassModeTranscript(transcript, isFinal, resultIndex) {
    // Same result index as the wake word trigger — Chrome revises interim transcripts
    // (e.g. "hey fana" → "hey comma show me coach assessments") within the same index.
    // For interim results, skip entirely. For the final result, strip the leading
    // wake-word portion and keep any user speech that followed.
    if (resultIndex === this.wakeWordTriggerResultIndex) {
      if (!isFinal) {
        // Interim at wake word index — skip (Chrome is still revising)
        return;
      }
      // Final at wake word index — this is Chrome's revised version of the wake
      // word utterance. Skip the whole thing. The user's actual command will come
      // as a separate result index.
      this.wakeWordTriggerResultIndex = -1;
      return;
    }

    if (isFinal) {
      this.resetBypassTimer();

      // Always check commands first — even if duplicate transcript
      const command = this.detectCommand(transcript);
      if (command) {
        this.handleCommand(command);
        return;
      }

      // Skip duplicate transcripts for regular text only
      if (this.lastFinalTranscript === transcript) {
        return;
      }
      this.lastFinalTranscript = transcript;

      if (transcript.trim().length > 0) {
        this.callbacks.onTranscript?.(transcript, true);
      }
    } else {
      // Interim results - don't show wake word
      if (transcript.trim().length > 0 && !this.detectWakeWord(transcript)) {
        this.callbacks.onInterim?.(transcript);
      }
    }
  }

  enterBypassMode() {
    this.isBypassMode = true;
    this.lastFinalTranscript = null;  // Fresh session — no stale duplicates
    this.resetBypassTimer();
  }

  resetBypassTimer() {
    // Clear any existing bypass timer
    if (this.bypassTimer) {
      clearTimeout(this.bypassTimer);
    }

    // Set bypass duration timer (15 seconds default)
    const duration = (this.settings.behavior?.bypassDuration || 15) * 1000;

    this.bypassTimer = setTimeout(() => {
      this.exitBypassMode();
      this.stopRecording();
    }, duration);
  }

  exitBypassMode() {
    this.isBypassMode = false;
    this.lastFinalTranscript = null; // Clear duplicate tracking
    if (this.bypassTimer) {
      clearTimeout(this.bypassTimer);
      this.bypassTimer = null;
    }
  }

  handleCommand(command) {
    switch (command.type) {
      case 'send':
        this.playSound('messageSent');
        this.callbacks.onSendCommand?.(command.text);
        this.stopRecording();
        break;

      case 'clear':
        this.playSound('recordStop');
        this.callbacks.onClearCommand?.();
        // Don't stop recording on clear - user might want to continue
        break;

      case 'stop':
        this.stopRecording();
        break;
      default:
        break;
    }
  }

  stopRecording() {
    this.isRecording = false;
    this.isBypassMode = false;
    this.playSound('recordStop');
    this.callbacks.onStopRecording?.();

    // Clear bypass timer
    if (this.bypassTimer) {
      clearTimeout(this.bypassTimer);
      this.bypassTimer = null;
    }
  }

  start(callbacks) {
    if (this.lowBatteryMode) {
      (callbacks || this.callbacks)?.onError?.('Voice disabled due to low battery');
      return false;
    }

    if (!this.recognition) {
      (callbacks || this.callbacks)?.onError?.('Speech recognition not supported in this browser');
      return false;
    }

    // Set callbacks — required on every start() call
    if (callbacks) {
      this.callbacks = callbacks;
    }
    this.isListening = true;

    // Acquire a persistent mic stream so macOS keeps the mic indicator
    // steady while SpeechRecognition restarts its sessions.
    if (!this._micStream) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => { this._micStream = stream; })
        .catch(err => console.warn('[FaanaVoiceService] getUserMedia failed (cosmetic only):', err.message));
    }

    try {
      this.recognition.start();
    } catch (error) {
      if (!error.message?.includes('already started')) {
        console.error('[FaanaVoiceService] Failed to start recognition:', error);
        this.callbacks.onError?.(error.message);
        return false;
      }
    }

    // Periodic restart to prevent Chrome's 60-second timeout
    if (this.periodicRestartTimer) clearInterval(this.periodicRestartTimer);
    this.periodicRestartTimer = setInterval(() => {
      if (this.isListening && !this.isRecording) {
        try { this.recognition.stop(); } catch (_e) { /* onend will restart */ }
      }
    }, 55000);

    return true;
  }

  stop() {
    this.isListening = false;  // Prevents onend from restarting
    this.isRecording = false;
    this.isBypassMode = false;

    if (this.bypassTimer) {
      clearTimeout(this.bypassTimer);
      this.bypassTimer = null;
    }
    if (this.periodicRestartTimer) {
      clearInterval(this.periodicRestartTimer);
      this.periodicRestartTimer = null;
    }

    if (this.recognition) {
      try { this.recognition.stop(); } catch (_e) { /* already stopped */ }
    }

    // Release the persistent mic stream
    if (this._micStream) {
      this._micStream.getTracks().forEach(track => track.stop());
      this._micStream = null;
    }
  }

  playSound(type) {
    if (this.settings.behavior?.feedbackSounds !== false) {
      try {
        const sound = this.sounds[type];
        if (sound) {
          sound.currentTime = 0;
          sound.play().catch(err => {
            console.log('Could not play sound:', err);
          });
        }
      } catch (error) {
        console.log('Could not play sound:', error);
      }
    }
  }

  speak(text, settings = {}) {
    console.log('[FaanaVoiceService] Speaking via Kokoro:', text.substring(0, 50) + '...');

    const voiceId = settings.voice || this.settings.tts?.voice || undefined;

    // Apply speed/volume to KokoroTTSService before speaking
    const speed = settings.speed || this.settings.tts?.speed || 1.0;
    const volume = settings.volume || this.settings.tts?.volume || 1.0;
    this._kokoroTTS.setSpeed(speed);
    this._kokoroTTS.setVolume(volume);

    // Bridge callbacks: ChatBot sets this.callbacks.onSpeakingStart/End/Error directly,
    // KokoroTTSService expects them as the third argument to speak().
    // The closure reads this.callbacks at call time (not capture time),
    // so it always uses the latest callbacks even during interruption.
    this._kokoroTTS.speak(text, voiceId, {
      onSpeakingStart: () => this.callbacks.onSpeakingStart?.(),
      onSpeakingEnd:   () => this.callbacks.onSpeakingEnd?.(),
      onSpeakingError: (err) => this.callbacks.onSpeakingError?.(err),
    });
  }

  stopSpeaking() {
    this._kokoroTTS.stop();
  }

  // ---------------------------------------------------------------------------
  // Progressive streaming speech — feed sentences during LLM streaming
  // ---------------------------------------------------------------------------

  /**
   * Begin a streaming speech session. Call this when streaming starts,
   * before any tokens arrive. Sentences are fed progressively via
   * feedStreamingText() as the LLM streams tokens.
   *
   * If the Kokoro model is still loading, this method enters buffer mode and
   * returns true immediately. feedStreamingText() accumulates text while
   * buffering. finalizeStreamingSpeech() waits for model readiness then
   * speaks the full buffered text — ensuring voice always responds even
   * when the model finishes loading after streaming begins.
   *
   * @param {object} [settings] - Optional TTS settings override
   * @returns {boolean} true if streaming session started (or buffering), false on error
   */
  beginStreamingSpeech(settings = {}) {
    const voiceId = settings.voice || this.settings.tts?.voice || undefined;
    const speed = settings.speed || this.settings.tts?.speed || 1.0;
    const volume = settings.volume || this.settings.tts?.volume || 1.0;

    this._kokoroTTS.setSpeed(speed);
    this._kokoroTTS.setVolume(volume);

    // Store current session settings for deferred use
    this._streamingVoiceId = voiceId;

    // If model is ready, start the streaming session immediately
    if (this._kokoroTTS.isReady()) {
      const started = this._kokoroTTS.beginStreaming(voiceId, {
        onSpeakingStart: () => this.callbacks.onSpeakingStart?.(),
        onSpeakingEnd:   () => this.callbacks.onSpeakingEnd?.(),
        onSpeakingError: (err) => this.callbacks.onSpeakingError?.(err),
      });

      if (!started) return false;

      this._spokenSentenceCount = 0;
      this._streamingBufferMode = false;
      this._streamingBufferedText = '';
      this._streamingBufferStopped = false;
      return true;
    }

    // Model not yet ready — enter buffer mode. Return true so ChatBot
    // treats streaming voice as active; text is buffered until model loads.
    console.log('[FaanaVoiceService] Kokoro model not ready — buffering streaming speech until model loads');
    this._streamingBufferMode = true;
    this._streamingBufferedText = '';
    this._streamingBufferStopped = false;
    this._spokenSentenceCount = 0;
    return true;
  }

  /**
   * Feed the accumulated streamed text so far. Extracts completed sentences
   * (all except the last fragment which may be incomplete) and enqueues any
   * new ones that haven't been spoken yet.
   *
   * In buffer mode (model still loading), simply records the latest full text.
   *
   * @param {string} fullTextSoFar - The full accumulated streamed text.
   */
  feedStreamingText(fullTextSoFar) {
    // In buffer mode, just record the latest full text. finalizeStreamingSpeech
    // will speak the complete response once the model is ready.
    if (this._streamingBufferMode) {
      this._streamingBufferedText = fullTextSoFar;
      return;
    }

    if (!fullTextSoFar) return;

    // Split on sentence boundaries AND structural breaks (colon+newline,
    // double newline). LLM responses often use colons and bullet points
    // instead of periods, so .!? alone misses boundaries during streaming.
    const parts = fullTextSoFar.split(/(?<=[.!?:])[ \t]*\n|\n\n+|(?<=[.!?])\s+/);

    // All parts except the last are complete sentences
    const completeSentences = parts.slice(0, -1);

    // Enqueue any new sentences we haven't spoken yet
    for (let i = this._spokenSentenceCount; i < completeSentences.length; i++) {
      const cleaned = stripMarkdown(completeSentences[i]).trim();
      if (cleaned) {
        this._kokoroTTS.enqueueSentence(cleaned);
      }
    }
    this._spokenSentenceCount = completeSentences.length;
  }

  /**
   * Finalize the streaming session — feed ALL remaining text (including the
   * last fragment which is now known to be complete) and signal end of stream.
   *
   * In buffer mode, waits for model readiness then speaks the complete text.
   *
   * @param {string} fullText - The complete final text.
   */
  finalizeStreamingSpeech(fullText) {
    // In buffer mode, the model was not ready when streaming started.
    // Speak the complete text once the model is ready.
    if (this._streamingBufferMode) {
      this._streamingBufferMode = false;
      const textToSpeak = fullText || this._streamingBufferedText;
      this._streamingBufferedText = '';

      if (!textToSpeak) return;

      if (this._kokoroTTS.isReady()) {
        // Model is ready — speak immediately
        if (!this._streamingBufferStopped) {
          console.log('[FaanaVoiceService] Model ready, speaking buffered response');
          this.speak(stripMarkdown(textToSpeak));
        }
      } else if (this._kokoroTTS._initPromise) {
        // Model still loading — wait for it then speak
        console.log('[FaanaVoiceService] Waiting for model to load before speaking buffered response');
        this._kokoroTTS._initPromise.then(() => {
          if (!this._streamingBufferStopped) {
            console.log('[FaanaVoiceService] Model loaded, speaking buffered response');
            this.speak(stripMarkdown(textToSpeak));
          }
        }).catch((err) => {
          console.warn('[FaanaVoiceService] Model failed to load, cannot speak buffered response:', err?.message);
        });
      }
      return;
    }

    const clean = stripMarkdown(fullText);
    if (!clean) {
      this._kokoroTTS.endStreaming();
      return;
    }

    if (this._spokenSentenceCount === 0) {
      // No sentences were progressively spoken during streaming —
      // bypass the queue and use the proven speak() method which handles
      // full multi-sentence text correctly via the worker's tts.stream().
      this._kokoroTTS.stop();
      this.speak(clean);
      return;
    }

    // Some sentences were already spoken progressively — enqueue the rest
    const parts = clean.split(/(?<=[.!?])\s+/);
    for (let i = this._spokenSentenceCount; i < parts.length; i++) {
      const sentence = parts[i].trim();
      if (sentence) {
        this._kokoroTTS.enqueueSentence(sentence);
      }
    }
    this._spokenSentenceCount = parts.length;

    // Signal no more sentences coming
    this._kokoroTTS.endStreaming();
  }

  /**
   * Cancel the streaming speech session. Used when tool calls reset the
   * content or the user stops streaming.
   */
  stopStreamingSpeech() {
    this._streamingBufferStopped = true;
    this._streamingBufferMode = false;
    this._streamingBufferedText = '';
    this._kokoroTTS.stop();
    this._spokenSentenceCount = 0;
  }

  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };

    // Update recognition language if changed
    if (this.recognition && newSettings.language) {
      this.recognition.lang = newSettings.language;
    }

    // Update wake word variations if changed
    if (newSettings.wakeWord) {
      this.wakeWordVariations = this.generateWakeWordVariations(newSettings.wakeWord);
    }

    // Forward TTS settings to Kokoro engine
    if (newSettings.tts?.speed !== undefined) {
      this._kokoroTTS.setSpeed(newSettings.tts.speed);
    }
    if (newSettings.tts?.volume !== undefined) {
      this._kokoroTTS.setVolume(newSettings.tts.volume);
    }

    // Restart recognition if active to apply new settings
    // stop() sets isListening=false which prevents onend restart,
    // then start() re-enables it cleanly
    if (this.isListening) {
      this.stop();
      this.start(this.callbacks);
    }
  }

  // Get current status
  getStatus() {
    return {
      isListening: this.isListening,
      isRecording: this.isRecording,
      isBypassMode: this.isBypassMode,
      isLowBattery: this.lowBatteryMode,
      batteryLevel: this.batteryLevel,
      browserSupported: !!this.recognition
    };
  }
}